import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { startCoinPayment } from "@/lib/coinpay";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, AiCard } from "@/components/perf";
import PageBackdrop from "@/components/PageBackdrop";
import ExpandableText from "@/components/ExpandableText";
import ShareMenu from "@/components/ShareMenu";
import { shareProductUrl, getRef } from "@/lib/shareRef";
import ShareEarnHint from "@/components/ShareEarnHint";
import ReturnPolicyButton from "@/components/ReturnPolicy";
import { canSeePage } from "@/lib/pages";
import { useContent } from "@/context/ContentContext";
import ProductGallery from "@/components/ProductGallery";
import ProductDetailModal from "@/components/ProductDetailModal";
import VariantPicker, { variantComplete, productImages, colorPhotoIndex, variantPrice } from "@/components/VariantPicker";
import { toast } from "sonner";
import { ShoppingCart, Plus, Sparkles, Loader2, ExternalLink, SlidersHorizontal } from "lucide-react";

const BASE_CATEGORIES = ["Paddles", "Balls", "Grips", "Training Aids", "Nets", "Apparel", "Bags", "Recovery"];

export default function Store() {
  const { user } = useAuth();
  const contentCtx = useContent();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All");
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(true);
  const [recBusy, setRecBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({ level: "All", price: "any", sale: false });
  const [picks, setPicks] = useState({});
  const [detail, setDetail] = useState(null);
  const { addProduct, setOpen, count, clear } = useCart();

  const addWithVariant = (p) => {
    if (!variantComplete(p, picks[p.id])) { setDetail({ p, i: 0 }); return toast.info("Pick your size / color first"); }
    addProduct(p, picks[p.id]);
  };
  const coinWithVariant = (p) => {
    if (!variantComplete(p, picks[p.id])) { setDetail({ p, i: 0 }); return toast.info("Pick your size / color first"); }
    startCoinPayment(navigate, { kind: "product", item_id: p.id, payload: { variant: picks[p.id] || {}, ref: getRef(user?.id) } });
  };

  const visible = products.filter((p) => {
    if (filters.level !== "All" && p.level !== filters.level && p.level !== "All") return false;
    if (filters.price === "under25" && p.price >= 25) return false;
    if (filters.price === "25to75" && (p.price < 25 || p.price > 75)) return false;
    if (filters.price === "over75" && p.price <= 75) return false;
    if (filters.sale && !p.on_sale) return false;
    return true;
  });
  const filtersActive = filters.level !== "All" || filters.price !== "any" || filters.sale;

  const loadProducts = async (c) => {
    const { data } = await api.get("/store/products", { params: { category: c } });
    setProducts(data);
  };
  useEffect(() => { loadProducts(cat); }, [cat]);
  useAutoRefresh(() => loadProducts(cat));
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/store/recommendation"); setRec(data); }
      finally { setRecLoading(false); }
    })();
  }, []);

  const genRec = async () => {
    setRecBusy(true);
    try {
      const { data } = await api.get("/store/recommendation", { params: rec?.advice ? { force: true } : { generate: true } });
      setRec(data);
      if (data.coins_charged > 0) toast.success(`Fresh gear pick — ${data.coins_charged} BLC`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't generate a pick"); }
    finally { setRecBusy(false); }
  };

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setVerifying(false); toast.error("Payment status check timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setVerifying(false);
        clear();
        toast.success("Payment successful — order confirmed!");
        loadProducts(cat);
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setVerifying(false); toast.error("Could not verify payment.");
    }
  }, [clear, cat]); // eslint-disable-line

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  if (!canSeePage(user, contentCtx?.content?.["access.store"])) return <Navigate to={user ? "/profile" : "/auth"} replace />;

  return (
    <div>
      <PageBackdrop ck="backdrop.store" image="https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/83d5cb6e2a0816d0000e126d7404b7a01e2b700bebb5ecd5b5e40b267a930c83.png" />
      <div className="relative z-10">
      {verifying && (
        <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center">
          <div className="bg-pb-surface border border-pb-cyan p-8 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-pb-cyan animate-spin" />
            <span className="font-display font-bold uppercase tracking-widest">Verifying Payment…</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 mb-2 animate-fade-up">
        <img src="/icons/proshop.png" alt="PBPA Pro Shop" data-testid="store-logo" className="w-20 h-20 sm:w-24 sm:h-24 object-contain drop-shadow-[0_0_18px_rgba(163,230,53,0.45)]" />
      </div>
      <PageHeader ck="page.store" kicker="PBPA Pro Shop · Performance-Linked Retail"
        title="Pro Shop"
        subtitle="Gear is not separate from training — it's part of progression. The AI recommends upgrades as your performance evolves.">
        <div className="flex items-center gap-3">
          <ShareEarnHint testid="store-share-earn" />
          <button data-testid="cart-toggle" onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 border border-pb-border px-4 py-3 hover:border-pb-red transition-colors">
            <ShoppingCart className="w-5 h-5" />
            <span className="font-bold uppercase text-sm tracking-widest">Cart</span>
            {count > 0 && <span className="absolute -top-2 -right-2 bg-pb-red text-black text-xs font-bold w-5 h-5 flex items-center justify-center">{count}</span>}
          </button>
        </div>
      </PageHeader>

      <div className="mb-6">
        <AiCard loading={recLoading}>
          {rec && (
            <div className="flex flex-wrap items-center gap-4">
              <Sparkles className="w-5 h-5 text-pb-cyan shrink-0" />
              {rec.advice ? (
                <p className="text-sm text-zinc-300 flex-1 min-w-[200px]" data-testid="store-rec-advice">{rec.advice}</p>
              ) : (
                <p className="text-sm text-zinc-500 flex-1 min-w-[200px]">Get an AI gear pick matched to your division and weakest area.</p>
              )}
              <Pill color="#4ADE80">{rec.division} fit</Pill>
              <button data-testid="store-gen-rec-btn" disabled={recBusy} onClick={genRec}
                className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
                <Sparkles className="w-3.5 h-3.5" />
                {recBusy ? "Generating…" : rec.advice ? `Refresh Pick · ${rec.refresh_fee_blc ?? 5} BLC` : "Generate AI Pick — Free"}
              </button>
            </div>
          )}
        </AiCard>
      </div>

      <div className="flex gap-2 overflow-x-auto mb-6 pb-2">
        {["All", ...BASE_CATEGORIES, ...[...new Set(products.map((p) => p.category))].filter((c) => c && !BASE_CATEGORIES.includes(c)).sort()].map((c) => (
          <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest whitespace-nowrap border transition-colors ${cat === c ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-500 hover:text-white"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 bg-pb-surface border border-pb-border px-4 py-3" data-testid="store-filter-box">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
          <SlidersHorizontal className="w-3.5 h-3.5 text-pb-red" /> Filter by
        </span>
        <select data-testid="filter-level" value={filters.level} onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          className="bg-pb-elevated border border-pb-border px-3 py-1.5 text-xs text-white focus:outline-none focus:border-pb-red">
          <option value="All">Any skill level</option>
          <option value="Low Intermediate">Low Intermediate</option>
          <option value="Intermediate">Intermediate</option>
          <option value="Advanced">Advanced</option>
        </select>
        <select data-testid="filter-price" value={filters.price} onChange={(e) => setFilters({ ...filters, price: e.target.value })}
          className="bg-pb-elevated border border-pb-border px-3 py-1.5 text-xs text-white focus:outline-none focus:border-pb-red">
          <option value="any">Any price</option>
          <option value="under25">Under $25</option>
          <option value="25to75">$25 – $75</option>
          <option value="over75">Over $75</option>
        </select>
        <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
          <input data-testid="filter-sale" type="checkbox" checked={filters.sale}
            onChange={(e) => setFilters({ ...filters, sale: e.target.checked })}
            className="accent-[#A3E635] w-4 h-4" />
          On Sale only
        </label>
        {filtersActive && (
          <button data-testid="filter-clear" onClick={() => setFilters({ level: "All", price: "any", sale: false })}
            className="text-[10px] uppercase tracking-widest text-pb-red hover:text-[#BEF264]">Clear</button>
        )}
        <span className="ml-auto text-xs text-zinc-500 font-mono" data-testid="filter-count">{visible.length} item{visible.length === 1 ? "" : "s"}</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {visible.map((p) => (
          <div key={p.id} data-testid={`product-${p.id}`} className={`bg-pb-surface border border-pb-border transition-all group flex flex-col ${p.sold_out ? "opacity-70" : "hover:-translate-y-1 hover:border-pb-red"}`}>
            <div className="aspect-square relative">
              <ProductGallery images={productImages(p)} alt={p.name} grayscale={p.sold_out} jumpTo={colorPhotoIndex(p, picks[p.id])}
                className="w-full h-full" testid={`gallery-${p.id}`} onOpen={(i) => setDetail({ p, i })} />
              {p.sold_out && (
                <span data-testid={`soldout-${p.id}`} className="absolute top-3 left-3 bg-black/90 text-red-400 border border-red-500 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 pointer-events-none">Sold Out</span>
              )}
              {!p.sold_out && p.on_sale && (
                <span data-testid={`sale-${p.id}`} className="absolute top-3 right-3 bg-pb-red text-black text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 pointer-events-none">-{p.discount_percent}%</span>
              )}
            </div>
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-center justify-between mb-2">
                <Pill color="#4ADE80">{p.category}</Pill>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{p.level}</span>
                  <ShareMenu url={shareProductUrl(p.id, user?.id)}
                    text={`🏓 ${p.name} — $${p.price} at the Pickle Bullpen Pro Shop`}
                    image={`${process.env.REACT_APP_BACKEND_URL}/api/share/products/${p.id}/image`} testId={`share-${p.id}`} placement="below" />
                </div>
              </div>
              <button onClick={() => setDetail({ p, i: 0 })} data-testid={`product-name-${p.id}`}
                className="font-display font-bold text-lg uppercase leading-tight text-left hover:text-pb-red transition-colors">{p.name}</button>
              <div className="text-xs text-zinc-500 mt-1 mb-3">
                <ExpandableText text={p.desc} lines={2} testid={`product-desc-${p.id}`} className="text-xs text-zinc-500" />
              </div>
              {(p.sizes?.length > 0 || p.colors?.length > 0 || p.options?.length > 0) && (
                <div className="mb-3"><VariantPicker product={p} value={picks[p.id]} onChange={(v) => setPicks((s) => ({ ...s, [p.id]: v }))} testid={`variant-${p.id}`} /></div>
              )}
              <div className="flex items-center justify-between mt-auto">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-black text-xl text-pb-red" data-testid={`price-${p.id}`}>{p.has_options && !picks[p.id]?.option ? <span className="text-[10px] text-zinc-500 font-sans font-normal uppercase tracking-widest mr-1">from</span> : null}${variantPrice(p, picks[p.id])}</span>
                  {p.on_sale && <span className="text-xs text-zinc-500 line-through">${p.original_price}</span>}
                </div>
                {p.ambassador_link ? (
                  <a data-testid={`buynow-${p.id}`} href={p.ambassador_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 bg-pb-red text-black px-3 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#BEF264] active:scale-95 transition-all">
                    Buy Now <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <button data-testid={`add-${p.id}`} onClick={() => addWithVariant(p)} disabled={p.sold_out}
                    title={variantComplete(p, picks[p.id]) ? "Add to cart" : "Choose size / color first"}
                    className="bg-pb-elevated border border-pb-border hover:bg-pb-red hover:border-pb-red px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-pb-elevated disabled:hover:border-pb-border">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
              {p.price_coins && !p.ambassador_link && (
                <button data-testid={`coin-buy-${p.id}`} disabled={p.sold_out}
                  onClick={() => coinWithVariant(p)}
                  className="w-full mt-3 border border-purple-400/60 text-purple-300 hover:bg-purple-400/10 font-bold uppercase tracking-widest py-2 text-xs active:scale-95 transition-all disabled:opacity-40">
                  🪙 Buy with Coin · {p.price_coins} BLC
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <ReturnPolicyButton testid="store-return-policy" className="mt-10" />
      {detail && (
        <ProductDetailModal product={detail.p} startIndex={detail.i} variant={picks[detail.p.id]}
          onVariant={(v) => setPicks((s) => ({ ...s, [detail.p.id]: v }))}
          onAdd={(v) => { addProduct(detail.p, v); setDetail(null); }}
          onCoin={(v) => startCoinPayment(navigate, { kind: "product", item_id: detail.p.id, payload: { variant: v, ref: getRef(user?.id) } })}
          onClose={() => setDetail(null)} />
      )}
      </div>
    </div>
  );
}
